<template>
  <BaseSelect
    ref="selectRef"
    v-bind="$attrs"
    :options="formattedOptions"
    :model-value="modelValue"
    :placeholder="placeholder"
    :searchable="true"
    :clearable="clearable"
    :with-button="withButton"
    :button-label="buttonLabel"
    :no-results-text="noResultsText"
    :external-search="true"
    :editable="editable"
    :edit-title="editTitle"
    @update:model-value="handleUpdate"
    @change="handleChange"
    @button-click="$emit('button-click')"
    @edit-option="openEditModal"
    @search="handleSearch"
    @clear-search="handleClearSearch"
    @dropdown-scroll-end="handleScrollEnd"
  />

  <AddVehicleModal
    v-if="isEditModalVisible"
    :vehicle-id="editingTrailerId"
    @vehicle-updated="handleTrailerUpdated"
    @close="closeEditModal"
  />
</template>

<script>
import { getCompanyId } from "@/utils/userUtils";
import vehiclesApi from "@/services/api/vehiclesApi";
import AddVehicleModal from "@/components/Vehicle/AddVehicleModal.vue";

export default {
  name: "TrailerSelect",

  inheritAttrs: false,

  components: {
    AddVehicleModal,
  },

  props: {
    modelValue: {
      type: [String, Number],
      default: null,
    },
    placeholder: {
      type: String,
      default: "Выберите прицеп",
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    withButton: {
      type: Boolean,
      default: false,
    },
    buttonLabel: {
      type: String,
      default: "Добавить новый прицеп",
    },
    noResultsText: {
      type: String,
      default: "Прицеп не найден",
    },
    excludeIds: {
      type: Array,
      default: () => [],
    },
    edoVehicleRole: {
      type: String,
      default: "",
    },
    editable: {
      type: Boolean,
      default: true,
    },
    // Прокидывается в BaseSelect для кнопки редактирования выбранного прицепа
    editTitle: {
      type: String,
      default: "Редактировать прицеп",
    },
  },

  emits: [
    "update:modelValue",
    "change",
    "button-click",
    "edit-option",
    "trailer-updated",
  ],

  data() {
    return {
      companyId: getCompanyId(),
      trailers: [],
      pagination: {
        page: 1,
        pages: 1,
        next: null,
      },
      dataIsLoading: false,
      searchQuery: "",
      preselectedOption: null,
      isSearchActive: false,
      isEditModalVisible: false,
      editingTrailerId: null,
    };
  },

  computed: {
    formattedOptions() {
      const options = this.trailers
        .filter((vehicle) => {
          if (this.modelValue && vehicle.id === this.modelValue)
            return true;
          return !this.excludeIds.includes(vehicle.id);
        })
        .map((vehicle) => ({
          value: vehicle.id,
          label: vehicle.selector_name,
        }));

      if (
        this.preselectedOption &&
        !this.isSearchActive &&
        !options.find(
          (o) =>
            String(o.value) === String(this.preselectedOption.value)
        )
      ) {
        return [this.preselectedOption, ...options];
      }

      return options;
    },
  },

  watch: {
    async modelValue(newVal, oldVal) {
      if (!newVal) {
        this.preselectedOption = null;
        return;
      }

      if (newVal === oldVal) return;

      if (!this.isSearchActive && !this.preselectedOption) {
        await this.fetchPreselectedOption();
      }
    },
  },

  methods: {
    handleUpdate(value) {
      if (!value) {
        this.preselectedOption = null;
      }

      this.$emit("update:modelValue", value);
    },

    handleChange(option) {
      if (option) {
        this.preselectedOption = {
          value: option.value,
          label: option.label,
        };
      } else {
        this.preselectedOption = null;
      }

      this.$emit("change", option ? option.value : null);
    },

    async handleSearch(searchData) {
      this.isSearchActive = true;
      this.searchQuery = searchData.query;
      await this.fetchTrailers(1);
    },

    async handleClearSearch() {
      this.isSearchActive = false;
      this.searchQuery = "";
      await this.fetchTrailers(1);
    },

    async handleScrollEnd() {
      if (
        this.pagination.page < this.pagination.pages &&
        !this.dataIsLoading
      ) {
        await this.fetchTrailers(this.pagination.page + 1);
      }
    },

    openEditModal(trailerId) {
      if (!trailerId) return;

      this.editingTrailerId = trailerId;
      this.isEditModalVisible = true;
      this.$emit("edit-option", trailerId);
    },

    closeEditModal() {
      this.isEditModalVisible = false;
      this.editingTrailerId = null;
    },

    handleTrailerUpdated(trailer) {
      const index = this.trailers.findIndex(
        (item) => String(item.id) === String(trailer.id)
      );

      if (index === -1) {
        this.trailers = [trailer, ...this.trailers];
      } else {
        this.trailers.splice(index, 1, trailer);
      }

      this.preselectedOption = {
        value: trailer.id,
        label: trailer.selector_name,
      };

      this.$nextTick(() => {
        this.$refs.selectRef?.reselectSelectedOption();
      });
      this.$emit("trailer-updated", trailer);
    },

    async fetchTrailers(page = 1) {
      const pageNumber = parseInt(page) || 1;

      try {
        this.dataIsLoading = true;

        const params = {
          page: pageNumber,
          "q[enabled]": "true",
        };

        if (this.edoVehicleRole) {
          params["q[edo_vehicle_role]"] = this.edoVehicleRole;
        } else {
          params["q[vehicle_type]"] = "trailer";
        }

        if (this.searchQuery) {
          params["q[search]"] = this.searchQuery;
        }

        const response = await vehiclesApi.index(
          this.companyId,
          params
        );

        if (pageNumber === 1) {
          this.trailers = response.data.result;
        } else {
          this.trailers = [...this.trailers, ...response.data.result];
        }

        const meta = response.data.pagination_meta;
        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          next: meta.next,
        };
      } catch (error) {
        console.error("Ошибка при загрузке прицепов:", error);
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchPreselectedOption() {
      if (!this.modelValue) return;

      const exists = this.trailers.find(
        (v) => String(v.id) === String(this.modelValue)
      );
      if (exists) return;

      try {
        const response = await vehiclesApi.show(
          this.companyId,
          this.modelValue
        );
        const vehicle = response.data.result;
        this.preselectedOption = {
          value: vehicle.id,
          label: vehicle.selector_name,
        };
      } catch (error) {
        console.error(
          "Ошибка при загрузке выбранного прицепа:",
          error
        );
      }
    },
  },

  async mounted() {
    await this.fetchTrailers(1);
    await this.fetchPreselectedOption();
  },
};
</script>
